import { useTeacherUnsavedChanges } from '../../../components/layout/TeacherWorkspace';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { AppShell } from '../../../components/layout/AppShell';
import { teacherV2NavItems } from '../../../components/layout/v2NavItems';
import { assessmentAuthoringApi, hooksV2Api } from '../../../services/apiV2';


type Stage = 'define' | 'do' | 'explain' | 'apply';
type ItemType = 'mcq_single' | 'mcq_multi' | 'free_response' | 'ordering' | 'matching' | 'artifact' | 'hands_on' | 'verbal';

interface AssessmentItem {
  id: string;
  type: ItemType;
  prompt: string;
  options?: string[];
  correct_index?: number;
  correct_indices?: number[];
  correct_order?: string[];
  pairs?: Record<string, string> | Array<{ left: string; right: string }>;
  required?: boolean;
  instructions?: string;
  skill_label?: string;
}

interface AssessmentSchema {
  schema_version: number;
  title: string;
  description: string;
  hook_id: number | '';
  stage: Stage;
  standards: string[];
  settings: { show_objective_results: boolean; allow_ai_guidance: boolean };
  items: AssessmentItem[];
}

const emptySchema = (): AssessmentSchema => ({
  schema_version: 1,
  title: '',
  description: '',
  hook_id: '',
  stage: 'define',
  standards: [],
  settings: { show_objective_results: true, allow_ai_guidance: true },
  items: [],
});

const normalizeRows = (value: any): any[] => (Array.isArray(value) ? value : Array.isArray(value?.results) ? value.results : []);

export const V2AssessmentBuilder: React.FC = () => {
  const [searchParams] = useSearchParams();
  const requestedAssessmentId = Number(searchParams.get('assessmentId')) || null;
  const [hooks, setHooks] = useState<any[]>([]);
  const [assessments, setAssessments] = useState<any[]>([]);
  const [assessmentId, setAssessmentId] = useState<number | null>(null);
  const [schema, setSchema] = useState<AssessmentSchema>(emptySchema());
  const [standardsText, setStandardsText] = useState('');
  const [importText, setImportText] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [showRepository, setShowRepository] = useState(false);
  const [repositoryCatalog, setRepositoryCatalog] = useState<any>({ projects: [], results: [] });
  const [repositoryProjectKey, setRepositoryProjectKey] = useState('');
  const [repositoryLoading, setRepositoryLoading] = useState(false);
  const [repositoryError, setRepositoryError] = useState('');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');
  const [errors, setErrors] = useState<Array<{ path: string; message: string }>>([]);

  const [savedDraft, setSavedDraft] = useState(JSON.stringify(emptySchema()));
  useTeacherUnsavedChanges(!busy && (JSON.stringify({...schema, standards: standardsText.split(/[\n,;]+/).map(value => value.trim()).filter(Boolean)}) !== savedDraft || !!importText.trim()));
  const loadList = async () => {
    const rows = await assessmentAuthoringApi.list();
    setAssessments(normalizeRows(rows));
  };

  useEffect(() => {
    const load = async () => {
      const [hookResponse] = await Promise.all([hooksV2Api.list(), loadList()]);
      setHooks(normalizeRows(hookResponse.data));
    };
    void load();
  }, []);

  const selected = useMemo(() => assessments.find((row) => row.id === assessmentId), [assessments, assessmentId]);

  const loadAssessment = (row: any) => {
    const next = row.draft_schema as AssessmentSchema;
    setAssessmentId(row.id);
    setSchema({ ...emptySchema(), ...next, settings: { ...emptySchema().settings, ...(next?.settings || {}) } });
    setSavedDraft(JSON.stringify({ ...emptySchema(), ...next, settings: { ...emptySchema().settings, ...(next?.settings || {}) } }));
    setStandardsText((next?.standards || []).map((value: any) => typeof value === 'string' ? value : value.code).filter(Boolean).join(', '));
    setErrors([]);
    setNotice('');
  };

  useEffect(() => {
    if (!requestedAssessmentId || assessmentId === requestedAssessmentId) return;
    const row = assessments.find((candidate) => candidate.id === requestedAssessmentId);
    if (!row) return;
    const next = row.draft_schema as AssessmentSchema;
    setAssessmentId(row.id);
    setSchema({ ...emptySchema(), ...next, settings: { ...emptySchema().settings, ...(next?.settings || {}) } });
    setSavedDraft(JSON.stringify({ ...emptySchema(), ...next, settings: { ...emptySchema().settings, ...(next?.settings || {}) } }));
    setStandardsText((next?.standards || []).map((value: any) => typeof value === 'string' ? value : value.code).filter(Boolean).join(', '));
    setErrors([]);
    setNotice('Opened from its controlled Lesson Runner delivery copy.');
  }, [assessmentId, assessments, requestedAssessmentId]);

  const startNew = () => {
    setAssessmentId(null);
    setSchema(emptySchema());
    setSavedDraft(JSON.stringify(emptySchema()));
    setStandardsText('');
    setErrors([]);
    setNotice('New draft');
  };

  const addItem = (type: ItemType) => {
    const usedIds = new Set(schema.items.map((item) => item.id));
    let nextNumber = schema.items.length + 1;
    while (usedIds.has(`item-${nextNumber}`)) nextNumber += 1;
    const id = `item-${nextNumber}`;
    setSchema((current) => ({
      ...current,
      items: [...current.items,
        type === 'mcq_single'
          ? { id, type, prompt: '', options: ['', ''], correct_index: 0 }
          : type === 'mcq_multi'
            ? { id, type, prompt: '', options: ['', ''], correct_indices: [0] }
            : type === 'ordering'
              ? { id, type, prompt: '', correct_order: ['', ''] }
              : type === 'matching'
                ? { id, type, prompt: '', pairs: [{ left: '', right: '' }, { left: '', right: '' }] }
                : type === 'artifact'
                  ? { id, type, prompt: '', required: true, instructions: '' }
                  : type === 'hands_on' || type === 'verbal'
                    ? { id, type, prompt: '', skill_label: '' }
                    : { id, type, prompt: '' },
      ],
    }));
  };

  const updateItem = (index: number, patch: Partial<AssessmentItem>) => {
    setSchema((current) => ({
      ...current,
      items: current.items.map((item, itemIndex) => itemIndex === index ? { ...item, ...patch } : item),
    }));
  };

  const removeItem = (index: number) => {
    setSchema((current) => ({ ...current, items: current.items.filter((_item, itemIndex) => itemIndex !== index) }));
  };

  const schemaForSave = (): AssessmentSchema => ({
    ...schema,
    standards: standardsText.split(/[\n,;]+/).map((value) => value.trim()).filter(Boolean),
  });

  const matchingRows = (item: AssessmentItem): Array<{ left: string; right: string }> => {
    if (Array.isArray(item.pairs)) return item.pairs;
    if (item.pairs && typeof item.pairs === 'object') return Object.entries(item.pairs).map(([left, right]) => ({ left, right }));
    return [];
  };

  const itemTypeLabel = (type: ItemType): string => ({
    mcq_single: 'Auto-graded MCQ',
    mcq_multi: 'Auto-graded multi-select',
    free_response: 'Teacher-reviewed response',
    ordering: 'Auto-graded ordering',
    matching: 'Auto-graded matching',
    artifact: 'Student artifact',
    hands_on: 'Teacher-observed hands-on skill',
    verbal: 'Teacher-verified verbal demonstration',
  }[type]);

  const save = async () => {
    setBusy(true);
    setErrors([]);
    setNotice('');
    try {
      const saved = assessmentId
        ? await assessmentAuthoringApi.update(assessmentId, schemaForSave())
        : await assessmentAuthoringApi.create(schemaForSave());
      setAssessmentId(saved.id);
      setSchema(saved.draft_schema);
      setSavedDraft(JSON.stringify(saved.draft_schema));
      setImportText('');
      setNotice('Draft saved. Publishing will create an immutable student-runner version.');
      await loadList();
    } catch (error: any) {
      setErrors(error?.response?.data?.errors || [{ path: 'assessment', message: error?.response?.data?.error || 'Could not save assessment.' }]);
    } finally {
      setBusy(false);
    }
  };

  const previewImport = async () => {
    setBusy(true);
    setErrors([]);
    try {
      const preview = await assessmentAuthoringApi.importPreview(importText);
      setAssessmentId(null);
      setSchema(preview.schema);
      setStandardsText((preview.schema.standards || []).map((value: any) => typeof value === 'string' ? value : value.code).filter(Boolean).join(', '));
      setShowImport(false);
      setNotice(`Valid ${String(preview.source_format).toUpperCase()} import loaded as an unsaved draft. Review it, then save.`);
    } catch (error: any) {
      setErrors(error?.response?.data?.errors || [{ path: 'import', message: 'Could not validate the import.' }]);
    } finally {
      setBusy(false);
    }
  };

  const loadRepositoryCatalog = async (projectKey = repositoryProjectKey) => {
    setRepositoryLoading(true);
    setRepositoryError('');
    try {
      const catalog = await assessmentAuthoringApi.repositoryCatalog(projectKey || undefined);
      setRepositoryCatalog(catalog || { projects: [], results: [] });
    } catch (error: any) {
      setRepositoryError(error?.response?.data?.detail || 'Could not load repository assessments.');
    } finally {
      setRepositoryLoading(false);
    }
  };

  const openRepository = async () => {
    const opening = !showRepository;
    setShowRepository(opening);
    if (opening) await loadRepositoryCatalog();
  };

  const loadRepositoryAssessment = async (entry: any) => {
    setRepositoryLoading(true);
    setRepositoryError('');
    setErrors([]);
    try {
      const preview = await assessmentAuthoringApi.repositoryPreview(
        entry.key,
        entry.project_key,
        entry.source_hash,
      );
      setAssessmentId(null);
      setSchema(preview.schema);
      setStandardsText((preview.schema.standards || []).map((value: any) => typeof value === 'string' ? value : value.code).filter(Boolean).join(', '));
      setShowRepository(false);
      setNotice(`Loaded ${entry.title} from the curriculum repository as an unsaved draft. Review the Hook, then save.`);
    } catch (error: any) {
      setRepositoryError(error?.response?.data?.detail || 'Could not validate the repository assessment.');
    } finally {
      setRepositoryLoading(false);
    }
  };

  const publish = async () => {
    if (!assessmentId) return;
    setBusy(true);
    setErrors([]);
    try {
      const result = await assessmentAuthoringApi.publish(assessmentId);
      setNotice(`Published version ${result.version}. Lesson plan #${result.lesson_plan_id} is ready to assign as an independent assessment.`);
      await loadList();
    } catch (error: any) {
      setErrors(error?.response?.data?.errors || [{ path: 'publish', message: error?.response?.data?.error || 'Could not publish.' }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell role="teacher" navItems={teacherV2NavItems('assessments')} title="Assessments">
      <div className="grid gap-5 xl:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex items-center justify-between gap-2">
            <h1 className="font-semibold text-gray-950">Assessment library</h1>
            <button type="button" onClick={startNew} className="rounded bg-indigo-700 px-3 py-1.5 text-sm font-semibold text-white">New</button>
          </div>
          <button type="button" onClick={() => setShowImport((value) => !value)} className="mt-3 w-full rounded border border-indigo-200 px-3 py-2 text-sm font-semibold text-indigo-800">Import JSON/YAML</button>
          <button type="button" onClick={() => { void openRepository(); }} className="mt-2 w-full rounded border border-emerald-200 px-3 py-2 text-sm font-semibold text-emerald-800">Choose from Repository</button>
          <div className="mt-4 space-y-2">
            {assessments.map((row) => (
              <button key={row.id} type="button" onClick={() => loadAssessment(row)} className={`w-full rounded border px-3 py-2 text-left text-sm ${assessmentId === row.id ? 'border-indigo-400 bg-indigo-50' : 'border-gray-200'}`}>
                <span className="block font-semibold text-gray-900">{row.title}</span>
                <span className="text-xs text-gray-500">{row.stage.toUpperCase()} · {row.latest_version ? `v${row.latest_version}` : 'draft'}</span>
              </button>
            ))}
          </div>
        </aside>

        <main className="space-y-4">
          {showRepository && (
            <section className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="font-semibold text-emerald-950">Repository assessments</h2>
                  <p className="mt-1 text-sm text-emerald-800">Canonical assessment YAML/JSON from active curriculum projects. Selecting one loads an unsaved draft for review.</p>
                </div>
                <select
                  aria-label="Repository curriculum project"
                  className="rounded border border-emerald-300 bg-white px-3 py-2 text-sm"
                  value={repositoryProjectKey}
                  onChange={(event) => {
                    const nextProject = event.target.value;
                    setRepositoryProjectKey(nextProject);
                    void loadRepositoryCatalog(nextProject);
                  }}
                >
                  <option value="">All active projects</option>
                  {(repositoryCatalog.projects || []).map((project: any) => (
                    <option key={project.project_key} value={project.project_key}>{project.project_title}</option>
                  ))}
                </select>
              </div>
              {repositoryError && <p className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{repositoryError}</p>}
              {repositoryLoading ? (
                <p className="mt-4 text-sm text-emerald-900">Loading repository assessments…</p>
              ) : (
                <div className="mt-4 space-y-2">
                  {(repositoryCatalog.results || []).map((entry: any) => (
                    <article key={entry.key} className="flex flex-wrap items-center justify-between gap-3 rounded border border-emerald-200 bg-white p-3">
                      <div>
                        <p className="font-semibold text-gray-950">{entry.title}</p>
                        <p className="text-xs text-gray-600">{entry.project_title} · {String(entry.stage).toUpperCase()} · {entry.item_count} items · {entry.scope}</p>
                        <p className="mt-1 font-mono text-[11px] text-gray-500">{entry.canonical_path}</p>
                      </div>
                      <button type="button" disabled={repositoryLoading} onClick={() => { void loadRepositoryAssessment(entry); }} className="rounded bg-emerald-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">Load draft</button>
                    </article>
                  ))}
                  {(repositoryCatalog.results || []).length === 0 && <p className="rounded border border-dashed border-emerald-300 p-4 text-sm text-emerald-900">No canonical assessment files were found for this project.</p>}
                </div>
              )}
            </section>
          )}
          {showImport && (
            <section className="rounded-lg border border-indigo-200 bg-indigo-50 p-4">
              <h2 className="font-semibold text-indigo-950">Import preview</h2>
              <p className="mt-1 text-sm text-indigo-800">Paste a canonical assessment document. Nothing is saved until validation succeeds and you choose Save draft.</p>
              <textarea rows={12} value={importText} onChange={(event) => setImportText(event.target.value)} className="mt-3 w-full rounded border border-indigo-200 bg-white p-3 font-mono text-xs" placeholder={'title: Network Check\nhook_id: 1\nstage: define\nitems: ...'} />
              <button type="button" disabled={busy} onClick={() => { void previewImport(); }} className="mt-3 rounded bg-indigo-700 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Validate and load preview</button>
            </section>
          )}

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="grid gap-4 md:grid-cols-2">
              <label className="text-sm font-medium">Title<input className="mt-1 w-full rounded border border-gray-300 px-3 py-2" value={schema.title} onChange={(event) => setSchema({ ...schema, title: event.target.value })} /></label>
              <label className="text-sm font-medium">Project / Hook (optional)<select className="mt-1 w-full rounded border border-gray-300 px-3 py-2" value={schema.hook_id} onChange={(event) => setSchema({ ...schema, hook_id: Number(event.target.value) || '' })}><option value="">No project connection</option>{hooks.map((hook) => <option key={hook.id} value={hook.id}>{hook.title}</option>)}</select></label>
              <label className="text-sm font-medium">Stage<select className="mt-1 w-full rounded border border-gray-300 px-3 py-2" value={schema.stage} onChange={(event) => setSchema({ ...schema, stage: event.target.value as Stage })}><option value="define">DEFINE</option><option value="do">DO</option><option value="explain">EXPLAIN</option><option value="apply">APPLY</option></select></label>
              <label className="text-sm font-medium">Idaho CTE standards<input className="mt-1 w-full rounded border border-gray-300 px-3 py-2" value={standardsText} onChange={(event) => setStandardsText(event.target.value)} placeholder={schema.stage === 'apply' ? 'Required for APPLY' : 'Optional'} /></label>
            </div>
            <label className="mt-4 block text-sm font-medium">Description<textarea rows={2} className="mt-1 w-full rounded border border-gray-300 px-3 py-2" value={schema.description} onChange={(event) => setSchema({ ...schema, description: event.target.value })} /></label>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              <label className="flex items-start gap-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
                <input type="checkbox" checked={schema.settings.show_objective_results} onChange={(event) => setSchema({ ...schema, settings: { ...schema.settings, show_objective_results: event.target.checked } })} />
                <span><span className="block font-medium">Show objective results to students</span><span className="block text-xs text-gray-600">When off, Elevate still auto-grades for teacher review but does not reveal scores, correctness, missed items, or answer keys to students.</span></span>
              </label>
              <label className="flex items-start gap-2 rounded border border-gray-200 bg-gray-50 p-3 text-sm">
                <input type="checkbox" checked={schema.settings.allow_ai_guidance} onChange={(event) => setSchema({ ...schema, settings: { ...schema.settings, allow_ai_guidance: event.target.checked } })} />
                <span><span className="block font-medium">Allow student AI guidance</span><span className="block text-xs text-gray-600">Controls student help, follow-up guidance, and missed-answer explanations for this assessment. Teacher advisory review remains separate.</span></span>
              </label>
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3"><div><h2 className="font-semibold text-gray-950">Items and evidence</h2><p className="text-sm text-gray-600">Combine objective, written, artifact, observed, and demonstrated evidence.</p></div><div className="flex flex-wrap gap-2"><button type="button" onClick={() => addItem('mcq_single')} className="rounded border px-3 py-2 text-sm font-semibold">MCQ</button><button type="button" onClick={() => addItem('mcq_multi')} className="rounded border px-3 py-2 text-sm font-semibold">Multi-select</button><button type="button" onClick={() => addItem('ordering')} className="rounded border px-3 py-2 text-sm font-semibold">Ordering</button><button type="button" onClick={() => addItem('matching')} className="rounded border px-3 py-2 text-sm font-semibold">Matching</button><button type="button" onClick={() => addItem('free_response')} className="rounded border px-3 py-2 text-sm font-semibold">Written</button><button type="button" onClick={() => addItem('artifact')} className="rounded border px-3 py-2 text-sm font-semibold">Artifact</button><button type="button" onClick={() => addItem('hands_on')} className="rounded border px-3 py-2 text-sm font-semibold">Hands-on</button><button type="button" onClick={() => addItem('verbal')} className="rounded border px-3 py-2 text-sm font-semibold">Verbal</button></div></div>
            <div className="mt-4 space-y-4">
              {schema.items.map((item, index) => (
                <article key={`${item.id}-${index}`} className="rounded border border-gray-200 bg-gray-50 p-4">
                  <div className="flex items-center justify-between"><span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{itemTypeLabel(item.type)}</span><button type="button" onClick={() => removeItem(index)} className="text-sm text-red-700">Remove</button></div>
                  <div className="mt-3 grid gap-3 md:grid-cols-[160px_1fr]"><label className="text-sm font-medium">Item ID<input className="mt-1 w-full rounded border px-3 py-2" value={item.id} onChange={(event) => updateItem(index, { id: event.target.value })} /></label><label className="text-sm font-medium">Prompt<textarea rows={2} className="mt-1 w-full rounded border px-3 py-2" value={item.prompt} onChange={(event) => updateItem(index, { prompt: event.target.value })} /></label></div>
                  {(item.type === 'mcq_single' || item.type === 'mcq_multi') && <div className="mt-3 space-y-2">{(item.options || []).map((option, optionIndex) => <div key={optionIndex} className="flex items-center gap-2"><input type={item.type === 'mcq_multi' ? 'checkbox' : 'radio'} name={`correct-${index}`} checked={item.type === 'mcq_multi' ? (item.correct_indices || []).includes(optionIndex) : item.correct_index === optionIndex} onChange={() => item.type === 'mcq_multi' ? updateItem(index, { correct_indices: (item.correct_indices || []).includes(optionIndex) ? (item.correct_indices || []).filter((value) => value !== optionIndex) : [...(item.correct_indices || []), optionIndex].sort() }) : updateItem(index, { correct_index: optionIndex })} aria-label={`Correct option ${optionIndex + 1}`} /><input className="flex-1 rounded border px-3 py-2 text-sm" value={option} onChange={(event) => updateItem(index, { options: (item.options || []).map((value, valueIndex) => valueIndex === optionIndex ? event.target.value : value) })} placeholder={`Option ${optionIndex + 1}`} />{(item.options || []).length > 2 && <button type="button" className="text-xs font-semibold text-red-700" onClick={() => { const nextOptions = (item.options || []).filter((_value, valueIndex) => valueIndex !== optionIndex); if (item.type === 'mcq_multi') { const nextCorrect = (item.correct_indices || []).filter((value) => value !== optionIndex).map((value) => value > optionIndex ? value - 1 : value); updateItem(index, { options: nextOptions, correct_indices: nextCorrect }); } else { const nextCorrect = item.correct_index === optionIndex ? 0 : Math.max(0, Number(item.correct_index || 0) - (Number(item.correct_index || 0) > optionIndex ? 1 : 0)); updateItem(index, { options: nextOptions, correct_index: nextCorrect }); } }}>Remove</button>}</div>)}<button type="button" className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-semibold" onClick={() => updateItem(index, { options: [...(item.options || []), ''] })}>Add option</button></div>}
                  {item.type === 'ordering' && <div className="mt-3 space-y-2"><p className="text-xs text-gray-600">Enter items in the correct order. Students receive a rearranged list.</p>{(item.correct_order || []).map((value, valueIndex) => <div key={valueIndex} className="flex gap-2"><span className="pt-2 text-sm font-semibold">{valueIndex + 1}</span><input className="flex-1 rounded border px-3 py-2 text-sm" value={value} onChange={(event) => updateItem(index, { correct_order: (item.correct_order || []).map((row, rowIndex) => rowIndex === valueIndex ? event.target.value : row) })} placeholder={`Ordered item ${valueIndex + 1}`} />{(item.correct_order || []).length > 2 && <button type="button" onClick={() => updateItem(index, { correct_order: (item.correct_order || []).filter((_row, rowIndex) => rowIndex !== valueIndex) })} className="text-xs text-red-700">Remove</button>}</div>)}<button type="button" onClick={() => updateItem(index, { correct_order: [...(item.correct_order || []), ''] })} className="rounded border px-3 py-1.5 text-xs font-semibold">Add ordered item</button></div>}
                  {item.type === 'matching' && <div className="mt-3 space-y-2">{matchingRows(item).map((pair, pairIndex) => <div key={pairIndex} className="grid gap-2 md:grid-cols-2"><input className="rounded border px-3 py-2 text-sm" value={pair.left} onChange={(event) => updateItem(index, { pairs: matchingRows(item).map((row, rowIndex) => rowIndex === pairIndex ? { ...row, left: event.target.value } : row) })} placeholder="Left item" /><input className="rounded border px-3 py-2 text-sm" value={pair.right} onChange={(event) => updateItem(index, { pairs: matchingRows(item).map((row, rowIndex) => rowIndex === pairIndex ? { ...row, right: event.target.value } : row) })} placeholder="Correct match" /></div>)}<button type="button" onClick={() => updateItem(index, { pairs: [...matchingRows(item), { left: '', right: '' }] })} className="rounded border px-3 py-1.5 text-xs font-semibold">Add pair</button></div>}
                  {item.type === 'artifact' && <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto]"><label className="text-sm font-medium">Upload instructions<input className="mt-1 w-full rounded border px-3 py-2" value={item.instructions || ''} onChange={(event) => updateItem(index, { instructions: event.target.value })} /></label><label className="flex items-center gap-2 self-end pb-2 text-sm"><input type="checkbox" checked={item.required !== false} onChange={(event) => updateItem(index, { required: event.target.checked })} />Required artifact</label></div>}
                  {(item.type === 'hands_on' || item.type === 'verbal') && <label className="mt-3 block text-sm font-medium">Teacher verification label<input className="mt-1 w-full rounded border px-3 py-2" value={item.skill_label || ''} onChange={(event) => updateItem(index, { skill_label: event.target.value })} placeholder="What the teacher will observe or verify" /></label>}
                </article>
              ))}
              {schema.items.length === 0 && <p className="rounded border border-dashed p-5 text-center text-sm text-gray-500">Add an item or import an assessment to begin.</p>}
            </div>
          </section>

          {errors.length > 0 && <section className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-900"><h2 className="font-semibold">Fix these issues</h2><ul className="mt-2 list-disc pl-5">{errors.map((row, index) => <li key={`${row.path}-${index}`}><strong>{row.path}:</strong> {row.message}</li>)}</ul></section>}
          {notice && <p className="rounded border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">{notice}</p>}
          <div className="flex flex-wrap gap-3"><button type="button" disabled={busy} onClick={() => { void save(); }} className="rounded bg-indigo-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Save draft</button><button type="button" disabled={busy || !assessmentId} onClick={() => { void publish(); }} className="rounded bg-emerald-700 px-4 py-2 font-semibold text-white disabled:opacity-50">Publish version</button>{selected?.published_lesson_plan_id && <><Link to={`/v2/teacher/lesson-plan/${selected.published_lesson_plan_id}/student-preview`} className="rounded border border-gray-300 px-4 py-2 font-semibold">Student preview</Link><Link to="/v2/teacher" className="rounded border border-gray-300 px-4 py-2 font-semibold">Assign published assessment</Link></>}</div>
          <p className="text-xs text-gray-500">Publishing creates an immutable lesson-plan version for the existing independent assessment runner. Headrush remains the official grade and mastery record.</p>
        </main>
      </div>
    </AppShell>
  );
};
